// data/zones/zone6.ts — THE GLOOMHEART.
// DATA ONLY. Numbers, tables, ASCII maps. The behaviour is content/zones/zone6.ts
// (the flood, the husks, the darkness mask, the brains) and
// content/bosses/boss6.ts (the Gloom Sovereign).
//
// DESIGN §5 Zone 6: "the deep remembers — a hub of three trials remixing the
// verbs you learned, on one unbroken clock."
//
// THE ANTI-RESKIN CONTRACT (D-008, stated in DESIGN so it cannot be mistaken for
// fatigue-reuse): this zone's new thing is not a roster. It is
//   · TOPOLOGY   — a hub of three spurs taken in any order (flow: 'spurs', D-021)
//   · THE CLOCK  — one Gloom meter across the whole zone, opening at 40% and
//                  never resetting: the question is which trial you take while
//                  you are still fresh
//   · COMPOSITION — every body is a body you have already met, digested: the
//                  same silhouettes, violet-wrong, at 30% elite rate, with the
//                  verbs of zones 2, 3 and 4 running one per spur.
//
// Bodies are re-dressed imports (ids namespaced 'z6-'), but every brain in this
// zone is written fresh in content/zones/zone6.ts. Importing another zone's DATA
// is reuse; running another zone's module state is a bug waiting to happen —
// zone 2's Tidecaller, for instance, refuses to hook when zone 2's own water
// cache is empty, which it always is once that zone has ended.

import type {
  EnemyDef,
  GeneratorDef,
  ProjectileDef,
  RoomTemplate,
  ZoneConfig,
  ZonePalette,
} from '../../types'
import { TUNING } from '../tuning'
import { ZONE2_ENEMIES } from './zone2'
import { ZONE3_ENEMIES } from './zone3'
import { ZONE4_ENEMIES, ZONE4_UNLIT } from './zone4'

// ---------------------------------------------------------------- palette
// Inside the living Gloom. Flesh-stone in deep violet-black, sickly glow
// accents, and nothing warm anywhere: torches 0, because the only light in the
// Gloomheart is the light you carry into it.

export const ZONE6_PALETTE: ZonePalette = {
  floor: '#241c33',
  floorAlt: '#2c2340',
  wall: '#3a2a52',
  wallEdge: '#6b4a9c',
  accent: '#a06bff',
  glow: '#d9a6ff',
  bg: '#07040e',
  torches: 0,
}

/** Zone-6 specific colours the mechanic and the boss paint with. */
export const ZONE6_COLORS = {
  flesh: '#2b2140',
  fleshLo: '#160f24',
  fleshHi: '#4a3568',
  vein: '#a06bff',
  veinDim: '#4b2b78',
  mote: '#c9a6ff',
  /** the digested memories of the earlier zones */
  water: '#141033',
  waterDeep: '#0a0722',
  waterEdge: '#5a3f9c',
  caustic: '#8f6bff',
  stone: '#3d3352',
  stoneTop: '#8a76b8',
  stoneShadow: '#0a0714',
  surge: '#b98cff',
  flood: '#7a4ad8',
  floodHot: '#e0c4ff',
  /** Readability law: unblockable damage carries a red edge. */
  floodEdge: '#ff7a42',
  hook: '#c07bff',
  huskGlow: '#dcc4ff',
  huskCore: '#5a4a80',
  tend: '#ff8ad0',
  dark: '#05030c',
  lamp: '#ffc46b',
  lampHot: '#fff0c4',
  ember: '#ff8a3c',
  emberDead: '#4a3a34',
  unlitBody: '#020106',
  unlitEye: '#e0cfff',
  homing: '#b98cff',
  sovereign: '#c9a6ff',
  sovereignCrown: '#e8dcae',
  heart: '#ff5c8a',
} as const

// ============================================================================
// THE ZONE'S OWN RULES
// ============================================================================

/**
 * DESIGN §5 Zone 6: "The Gloom does not reset in this zone. One meter across all
 * spurs + interstitials, and it STARTS AT 40%."
 *
 * The reset is per-zone and automatic (game/gloom.ts); the opening 40% is the
 * mechanic's onZoneStart, and it is the first thing the zone says.
 */
export const ZONE6_GLOOM = {
  /** Fraction the zone opens on. */
  openAt: 0.4,
  /** Bark the moment the meter lands. */
  openBark: 'The clock here started without you.',
  /** How hard the zone's persistent pulse reads at 0% and at 100% gloom. */
  pulseMin: 0.06,
  pulseMax: 0.34,
} as const

/**
 * THE DROWNING SPUR — zone 2's verb, digested. The surge runs on a FASTER
 * rhythm than the Drowned Roots' 25s: this is the exam, not the lesson.
 */
export const ZONE6_DROWN = {
  /** Multiplier on the Warden's move speed while standing on a 'W' tile. */
  slowMul: 0.72,
  /** …and while that water is actually surging. */
  floodSlowMul: 0.55,
  surge: {
    /** DESIGN's ~25s, tightened for the Gloomheart. */
    period: 18,
    /** Spur seconds before the first telegraph — long enough to read the room. */
    firstDelay: 8,
    telegraph: 3,
    duration: 4,
    damagePerSecond: 15,
    /** Spaced just above TUNING.player.contactIframes so 15 dmg/s means it. */
    damageTickSeconds: 0.65,
    /** Current shove: impulse px/s, and how often it lands. Named to match the
     *  Sovereign's edge floods — both go through the same z6FloodHurt. */
    push: 215,
    pushTickSeconds: 0.25,
    shake: 3.2,
  },
  /** Which way the spur's flood runs (normalised at use). */
  current: { x: 0.35, y: 1 },
} as const

/**
 * THE PALE SPUR — zone 3's verb, digested. Husks re-form in TWO seconds, not
 * four, and the Gravetenders arrive in pairs.
 */
export const ZONE6_PALE = {
  /** DESIGN §5 Zone 6: "Courtiers/Wailers with 2s husk timers". */
  huskSeconds: 2,
  /** …and a tended husk is back almost at once. */
  tendedSeconds: 0.8,
  /** Seconds a Gravetender must stand over a husk to tend it. */
  tendChannel: 1.1,
  /** DESIGN §5 Zone 3's rule, carried down: every resurrection darkens the zone. */
  reformGloom: 0.03,
  sealEmbers: TUNING.economy.huskSealEmber,
  walkSealSlack: 6,
  huskId: 'z6-husk',
  huskSources: ['z6-courtier', 'z6-wailer'] as string[],
  /** A re-formed body pays nothing (zone 3's D-flagged reading, kept). */
  reformedSuffix: '_r',
  /** How close a Gravetender has to be to start tending. */
  tendRange: 26,
} as const

/**
 * THE DARK SPUR — zone 4's verb, digested. Near-total dark, TWO braziers, and
 * the Unlit does not wait 45 seconds: the spur is 90 seconds long, so it walks
 * from the moment the gullet opens.
 */
export const ZONE6_DARK = {
  /** Tighter than the Gloaming Mines' 4.5 tiles: the Gloomheart eats light. */
  lampRadius: 128,
  lampCore: 0.5,
  brazier: {
    radius: 150,
    core: 0.55,
    burnSeconds: 20,
    gutterSeconds: 3,
    reach: 52,
    /** E is a flick here and in the arena — the Sovereign is pressure enough. */
    lightSeconds: 0.35,
  },
  unlit: {
    defId: 'z6-unlit',
    /** DESIGN §5 Zone 6: "the Unlit walks here" — from the moment it opens. */
    spawnAtSeconds: 0,
    spawnMinDistance: 380,
    speed: ZONE4_UNLIT.speed,
    touchDamage: ZONE4_UNLIT.touchDamage,
    recoilDistance: ZONE4_UNLIT.recoilDistance,
    recoilSeconds: ZONE4_UNLIT.recoilSeconds,
    banishSeconds: ZONE4_UNLIT.banishSeconds,
    banishRadius: ZONE4_UNLIT.banishRadius,
    sinkSeconds: ZONE4_UNLIT.sinkSeconds,
    riseSeconds: ZONE4_UNLIT.riseSeconds,
    droneFar: ZONE4_UNLIT.droneFar,
    droneNear: ZONE4_UNLIT.droneNear,
    droneRange: ZONE4_UNLIT.droneRange,
  },
} as const

// ---------------------------------------------------------------- projectiles

export const ZONE6_PROJECTILES: ProjectileDef[] = [
  // The Wailer's grief keeps its taught colour — area denial reads green, and a
  // ring you have already learned to thread must still look like one.
  {
    id: 'z6-griefshard',
    kind: 'straight',
    speed: 82,
    damage: 8,
    radius: 7,
    color: '#a8e878',
    lifetime: 4.2,
  },
  // Homing reads violet (DESIGN §4). The Deep Caster's bolt, gloom-warped.
  {
    id: 'z6-deepbolt',
    kind: 'homing',
    speed: 210,
    damage: 9,
    radius: 6,
    color: ZONE6_COLORS.homing,
    lifetime: 4.2,
    homingTurn: 1.6,
  },
]

// ---------------------------------------------------------------- enemies
// Every body here is a body from zones 1-4, re-dressed. The stats come from the
// source def; the ids, the palettes and the brains are ours. `ai.custom` points
// at brains registered in content/zones/zone6.ts — never at another zone's.

const Z2 = (id: string): EnemyDef => ZONE2_ENEMIES.find((e) => e.id === id) as EnemyDef
const Z3 = (id: string): EnemyDef => ZONE3_ENEMIES.find((e) => e.id === id) as EnemyDef
const Z4 = (id: string): EnemyDef => ZONE4_ENEMIES.find((e) => e.id === id) as EnemyDef

const MIRESKIPPER = Z2('mireskipper')
const SHELLBACK = Z2('shellback')
const TIDECALLER = Z2('tidecaller')
const COURTIER = Z3('courtier_blade')
const WAILER = Z3('wailer')
const GRAVETENDER = Z3('gravetender')
const DELVER = Z4('delver')
const GLINTMOTH = Z4('glintmoth')
const DEEPCASTER = Z4('deep_caster')

export const ZONE6_ENEMIES: EnemyDef[] = [
  // --- the Drowning Spur's memory of zone 2 --------------------------------
  {
    ...MIRESKIPPER,
    id: 'z6-skipper',
    name: 'Drowned Skipper',
    ai: { ...MIRESKIPPER.ai, custom: 'z6-skipper' },
    sprite: { key: 'mireskipper', size: 26, params: { skin: '#4a3a6b', belly: '#c9b6ef', eye: '#c07bff' } },
    emberValue: 1,
  },
  {
    ...SHELLBACK,
    id: 'z6-shellback',
    name: 'Gloomed Shellback',
    ai: { ...SHELLBACK.ai, custom: 'z6-shellback' },
    sprite: { key: 'shellback', size: 34, params: { shell: '#3c3550', rim: '#b9a6dd', flesh: '#6b5a7a' } },
    emberValue: 3,
  },
  {
    ...TIDECALLER,
    id: 'z6-tidecaller',
    name: 'Gloomed Tidecaller',
    ai: { ...TIDECALLER.ai, custom: 'z6-tidecaller', hookCooldown: 4.4 },
    sprite: { key: 'tidecaller', size: 34, params: { robe: '#241a3a', trim: '#c07bff', hook: '#d9bcff' } },
    emberValue: 4,
  },

  // --- the Pale Spur's memory of zone 3 ------------------------------------
  {
    ...COURTIER,
    id: 'z6-courtier',
    name: 'Digested Courtier',
    ai: { ...COURTIER.ai, custom: 'z6-courtier' },
    sprite: { key: 'z3_courtier', size: 32, params: { cloth: '#2e2340', bone: '#c9b6d8', glint: '#ff4a4a' } },
    emberValue: 3,
  },
  {
    ...WAILER,
    id: 'z6-wailer',
    name: 'Digested Wailer',
    ai: { ...WAILER.ai, custom: 'z6-wailer', projectile: 'z6-griefshard' },
    sprite: { key: 'z3_wailer', size: 32, params: { shroud: '#382c52', veil: '#bdb0d0', grief: '#a8e878' } },
    emberValue: 4,
  },
  {
    ...GRAVETENDER,
    id: 'z6-gravetender',
    name: 'Digested Gravetender',
    ai: { ...GRAVETENDER.ai, custom: 'z6-gravetender' },
    sprite: { key: 'z3_gravetender', size: 30, params: { robe: '#3a2f52', bone: '#c9b6d8', lamp: '#ff8ad0' } },
    emberValue: 5,
  },

  // --- the Dark Spur's memory of zone 4 ------------------------------------
  {
    ...DELVER,
    id: 'z6-delver',
    name: 'Swallowed Delver',
    ai: { ...DELVER.ai, custom: 'z6-delver' },
    sprite: { key: 'z4_delver', size: 30, params: { skin: '#5a5068', cloth: '#2c2438', lamp: '#c07bff' } },
    emberValue: 2,
  },
  {
    ...DEEPCASTER,
    id: 'z6-caster',
    name: 'Swallowed Caster',
    // The stock ranged archetype (game/enemies.ts) — shared code, not another
    // zone's module: the Deep Caster never had a custom brain to borrow.
    ai: { ...DEEPCASTER.ai, custom: undefined, projectile: 'z6-deepbolt' },
    sprite: { key: 'bonecaster', size: 30, params: { color: '#332a4a', glow: ZONE6_COLORS.homing } },
    emberValue: 4,
  },
  {
    ...GLINTMOTH,
    id: 'z6-moth',
    name: 'Gloommoth',
    ai: { ...GLINTMOTH.ai, custom: 'z6-moth' },
    sprite: { key: 'z4_glintmoth', size: 22, params: { wing: '#a98ce8', eye: '#d9bcff' } },
    emberValue: 1,
  },
  {
    // THE UNLIT, still walking. Its rules live in content/zones/zone6.ts; here it
    // is only a body with a place in the world. It pays nothing and cannot die.
    id: 'z6-unlit',
    name: 'The Unlit',
    role: 'stalker',
    hp: 99999,
    speed: ZONE6_DARK.unlit.speed,
    radius: 15,
    touchDamage: ZONE6_DARK.unlit.touchDamage,
    ai: { custom: 'z6-unlit' },
    sprite: { key: 'z4_unlit', size: 44, params: { eye: ZONE6_COLORS.unlitEye } },
    emberValue: 0,
    goldChance: 0,
  },
  {
    // THE HUSK. Zone 3's reading, kept whole: a real body so that every weapon,
    // splash, trail and potion already knows what "one hit seals it" means, and
    // tagged `gloomspawn` at spawn so it can never hold a room hostage or pay a
    // kill. emberValue MUST stay 0 — the seal pays once, by hand (D-012/D-015).
    id: 'z6-husk',
    name: 'Gloom Husk',
    role: 'swarm',
    hp: 1,
    speed: 0,
    radius: 11,
    touchDamage: 0,
    ai: { custom: 'z6-husk' },
    sprite: { key: 'z3_husk', size: 30, params: { glow: ZONE6_COLORS.huskGlow, core: ZONE6_COLORS.huskCore } },
    emberValue: 0,
    goldChance: 0,
  },
]

// ------------------------------------------------- the re-formed twins
// Trivial table builder (ARCHITECTURE §3 allows those in data): the same body,
// the same brain, the same NAME so the summary groups them under one family —
// and zero payout, because standing back up is not a second life's worth of
// embers (zone 3's flagged reading, carried down).

export const ZONE6_REFORMED: EnemyDef[] = ZONE6_ENEMIES.filter(
  (e) => ZONE6_PALE.huskSources.indexOf(e.id) >= 0,
).map((def) => ({
  ...def,
  id: def.id + ZONE6_PALE.reformedSuffix,
  emberValue: 0,
  goldChance: 0,
}))

// ---------------------------------------------------------------- generators
// Six sources, two per spur, each keeping the violet source-rift the whole game
// uses — in this zone the rift is not a decoration, it is what the walls are
// made of.

export const ZONE6_GENERATORS: GeneratorDef[] = [
  {
    id: 'z6-gen-root',
    name: 'Digested Root-Cluster',
    hp: 74,
    radius: 15,
    spawns: 'z6-skipper',
    interval: 3.0,
    burst: 2,
    cap: 6,
    sprite: { key: 'gen_rootcluster', size: 38, params: { root: '#5a3f8f', glow: '#c07bff' } },
    emberValue: 4,
  },
  {
    id: 'z6-gen-well',
    name: 'Digested Well',
    hp: 112,
    radius: 16,
    spawns: 'z6-shellback',
    interval: 6.0,
    burst: 1,
    cap: 3,
    sprite: { key: 'gen_drownedwell', size: 42, params: { stone: '#443a63' } },
    emberValue: 5,
  },
  {
    id: 'z6-gen-reliquary',
    name: 'Digested Reliquary',
    hp: 112,
    radius: 16,
    spawns: 'z6-courtier',
    interval: 5.2,
    burst: 1,
    cap: 3,
    sprite: { key: 'z3_gen_reliquary', size: 42, params: { bone: '#cabbd8', cloth: '#5a3f6e' } },
    emberValue: 5,
  },
  {
    id: 'z6-gen-organ',
    name: 'Digested Bone-Organ',
    hp: 94,
    radius: 16,
    spawns: 'z6-wailer',
    interval: 6.8,
    burst: 1,
    cap: 2,
    sprite: { key: 'z3_gen_boneorgan', size: 42, params: { bone: '#c9bdd4', pipe: '#9b8fb0' } },
    emberValue: 5,
  },
  {
    id: 'z6-gen-shaft',
    name: 'Swallowed Shaft',
    hp: 84,
    radius: 16,
    spawns: 'z6-delver',
    interval: 4.4,
    burst: 1,
    cap: 4,
    sprite: { key: 'gen_collapsedshaft', size: 42, params: { rock: '#3a2a52', timber: '#33243a' } },
    emberValue: 5,
  },
  {
    id: 'z6-gen-idol',
    name: 'Swallowed Idol',
    hp: 88,
    radius: 14,
    spawns: 'z6-caster',
    interval: 7.0,
    burst: 1,
    cap: 2,
    sprite: { key: 'gen_casteridol', size: 40, params: { stone: '#2f2444', eye: ZONE6_COLORS.homing } },
    emberValue: 6,
  },
]

// ============================================================================
// ROOMS — the Heartchamber and its three gullets (D-021)
// ============================================================================
//
// THE HUB is the zone's respite template: game/zones.ts recognises it by its
// altar glyph, and its ordinary 'D' doors map to ZONE6.spurIds BY READING ORDER.
// That order is load-bearing, so it is written here once and asserted in
// test/zone6.mjs:
//
//   door 0 — (0,6)  the west gullet   -> z6-drowning-spur
//   door 1 — (33,6) the east gullet   -> z6-pale-spur
//   door 2 — (17,17) the south gullet -> z6-dark-spur
//
// The altar and the fountain stand in the chamber itself (DESIGN §5 Zone 6:
// "the Heartchamber itself holds the zone's altar + fountain, usable between
// spurs — spend your window wisely; the clock runs"). The Gloom holds here by
// respite semantics and runs everywhere else: one unbroken meter, with the
// chamber as the only breath in it.
//
// The spurs are 44x26 like every other combat room in the game, so the camera
// reads the same and only the rules differ. There is no timed vault anywhere in
// this zone — no 'V', no 'v' — because the zone IS the clock.

export const ZONE6_ROOMS: RoomTemplate[] = [
  {
    id: 'z6-heartchamber',
    generators: [],
    patrols: [],
    map: [
      '#################S################',
      '#................................#',
      '#....####................####....#',
      '#....####................####....#',
      '#................................#',
      '#................................#',
      'D.........##..........##.........D',
      '#.........##..........##.........#',
      '#................................#',
      '#.......A...............N........#',
      '#................................#',
      '#................................#',
      '#................................#',
      '#....####................####....#',
      '#....####........@.......####....#',
      '#................................#',
      '#................................#',
      '#################D################',
    ],
  },

  // ------------------------------------------------------- THE DROWNING SPUR
  // A drowned causeway of zone 2, half-eaten: the road is still there, broken,
  // and everything either side of it is ankle-deep. Raised stone is sparse on
  // purpose — the surge runs every 18 seconds and there is nowhere generous to
  // stand it out.
  {
    id: 'z6-drowning-spur',
    generators: ['z6-gen-root', 'z6-gen-well'],
    // reading order: (5,12) (10,12) (33,13) (21,16) (23,16) (38,21)
    // the Tidecaller PAIR works the causeway from both ends.
    patrols: ['z6-shellback', 'z6-tidecaller', 'z6-tidecaller', 'z6-skipper', 'z6-skipper', 'z6-shellback'],
    map: [
      '############################################',
      '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
      '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
      '#WWRRRRRRWWWWWWWWWWWWWFWWWWWWWWWWWWRRRRRRWW#',
      '#WWRR@RRRWWWWWWWWWWWWWWWWWWWWWWWWWWRRRRRRWW#',
      '#WWRRRRRRWWW####WWWWWWWWWWWW####WWWRRRGRRWW#',
      '#WWRRRRRRWWW####WWWWWWWWWWWW####WWWRRRRRRWW#',
      '#WWWWWWWWWWW####WWWWWWWWWWWW####WWWWWWWWWWW#',
      '#WWWWWWWWWWW####WWWWWWWWWWWW####WWWWWWWWWWW#',
      '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
      '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
      '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
      '#WWRRERRRRERRRRRRRWWWRRRRRRRRRRRRRRRRRRRRWW#',
      '#WWRRRRRRRRRRRRRRRWWWRRRRRRRRRRRRERRRRRRRWW#',
      '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
      '#WWWWWWWWWWWWWWWWWWRRRRRRWWWWWWWWWWWWWWWWWW#',
      '#WWWWWWWWWWWWWWWWWWRRERERWWWWWWWWWWWWWWWWWW#',
      '#WWWWWWWWWWWWWWWWWWRRRRRRWWWWWWWWWWWWWWWWWW#',
      '#WWWWWWWWWWW####WWWWWWWWWWWW####WWWWWWWWWWW#',
      '#WWRRRRRRWWW####WWWWWWWWWWWW####WWWRRRRRRWW#',
      '#WWRRRGRRWWW####WWWWWWWWWWWW####WWWRRRRRRWW#',
      '#WWRRRRRRWWW####WWWWWWWWWWWW####WWWRRRERRWW#',
      '#WWRRRRRRWWWWWWWWWWWWWWWWWWWWWWWWWWRRRRRRWW#',
      '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
      '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
      '######################D#####################',
    ],
  },

  // ----------------------------------------------------------- THE PALE SPUR
  // The Pale Court's arches, tilted and sunk into the flesh-stone, with the
  // king's bier swallowed at the centre. Husks re-form in two seconds here, and
  // the Gravetenders come in pairs.
  {
    id: 'z6-pale-spur',
    generators: ['z6-gen-reliquary', 'z6-gen-organ'],
    // reading order: (30,3) (18,6) (38,8) (6,18) (25,18) (12,22)
    patrols: ['z6-courtier', 'z6-gravetender', 'z6-wailer', 'z6-wailer', 'z6-gravetender', 'z6-courtier'],
    map: [
      '############################################',
      '#..........................................#',
      '#..@.......................................#',
      '#.............................E............#',
      '#......##......##..........##......##......#',
      '#......##......##..........##......##......#',
      '#......##.G....##.E........##......##......#',
      '#......##......##..........##......##......#',
      '#......##......##..........##......##.E....#',
      '#..........................................#',
      '#...................####...................#',
      '#..####.............####...................#',
      '#..####.............####...................#',
      '#...................####.............####..#',
      '#...................####.............####..#',
      '#..........................................#',
      '#......##......##..........##......##......#',
      '#......##......##..........##......##......#',
      '#.....E##......##........E.##...G..##......#',
      '#......##......##..........##......##......#',
      '#......##......##..........##......##......#',
      '#..........................................#',
      '#...........E........F.....................#',
      '#..........................................#',
      '#..........................................#',
      '######################D#####################',
    ],
  },

  // ----------------------------------------------------------- THE DARK SPUR
  // Mine struts and cribbing, digested. Two braziers in the whole room, and
  // something that weapons cannot touch walking from the moment you step in.
  {
    id: 'z6-dark-spur',
    generators: ['z6-gen-idol', 'z6-gen-shaft'],
    // reading order: (21,3) (29,6) (12,8) (34,20)
    patrols: ['z6-delver', 'z6-caster', 'z6-delver', 'z6-caster'],
    map: [
      '############################################',
      '#..........................................#',
      '#..........................................#',
      '#..@.................E.....................#',
      '#.....##......##......##......##.....##....#',
      '#.....##......##......##......##.....##....#',
      '#............................E.............#',
      '#................###................G......#',
      '#...........E....###.......................#',
      '#................###.............B.........#',
      '#.....##......##.###..##......##.....##....#',
      '#.....##......##.###..##......##.....##....#',
      '#................###.......................#',
      '#..........B.....###.....###...............#',
      '#........................###...............#',
      '#........................###...............#',
      '#....F##......##......##.###..##.....##....#',
      '#.....##......##......##.###..##.....##....#',
      '#........................###...............#',
      '#........................###...............#',
      '#.................................E........#',
      '#.....##......##......##......##.....##....#',
      '#.....##......##......##......##.....##....#',
      '#.........G................................#',
      '#..........................................#',
      '######################D#####################',
    ],
  },
]

// ============================================================================
// BOSS 6 — THE GLOOM SOVEREIGN (DESIGN §5 Zone 6)
// "Verb: the final exam — it wields every zone's rule against you, tuned to what
// you brought." Every literal the fight reads lives here.
// ============================================================================

// ---------------------------------------------------------------- boss arena
// The Heartchamber transformed: the same architecture, the three gullets fused
// shut, and the chamber's edges gone to flesh-water. 44x21 so the whole fight
// fits the camera (D-016). Two braziers stand in it, dead, for the Memory of
// the Dark; the interior is dry ground until the Sovereign takes it.
export const ZONE6_BOSS_ARENA: RoomTemplate = {
  id: 'z6-sovereign-arena',
  generators: [],
  patrols: [],
  map: [
    '############################################',
    '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
    '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
    '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
    '#WWWWW................................WWWWW#',
    '#WWWWW..###......................###..WWWWW#',
    '#WWWWW..###......................###..WWWWW#',
    '#WWWWW................................WWWWW#',
    '#WWWWW................................WWWWW#',
    '#WWWWW................................WWWWW#',
    '#WWWWW......B..................B......WWWWW#',
    '#WWWWW................................WWWWW#',
    '#WWWWW................................WWWWW#',
    '#WWWWW................................WWWWW#',
    '#WWWWW..###......................###..WWWWW#',
    '#WWWWW..###...........@..........###..WWWWW#',
    '#WWWWW................................WWWWW#',
    '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
    '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
    '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
    '######################D#####################',
  ],
}

// --------------------------------------------- the husk-heart, as a body
// A Crown-class part (boss 3's lesson): an ordinary enemy with a custom brain,
// so every weapon, splash, burn trail, potion and Maul-stagger already knows
// what to do with it. Its HP is written by the boss at spawn so the zone's stat
// scale can never drift the window. emberValue MUST stay 0 — it is a phase of a
// boss, not loot.
export const ZONE6_BOSS_ENEMIES: EnemyDef[] = [
  {
    id: 'z6-huskheart',
    name: 'The Husk-Heart',
    role: 'stalker',
    hp: 80,
    speed: 0,
    radius: 17,
    touchDamage: 0,
    ai: { custom: 'z6-huskheart' },
    sprite: { key: 'z6_huskheart', size: 36, params: { flesh: '#5a2340', vein: ZONE6_COLORS.vein } },
    emberValue: 0,
    goldChance: 0,
  },
]

/**
 * Boss tuning block. Arrays indexed by phase-1 (P1, P2, P3).
 *
 * FLAGGED INTERPRETATION (`escorts`): DESIGN §5 Boss 6 names four counters by
 * weapon form. The read is at FIGHT START and it is announced — the Sovereign
 * does not re-read mid-fight, because a build-reading boss that keeps changing
 * its answer is noise rather than an exam. A form with no entry (a future
 * weapon) falls back to the blade's.
 *
 * FLAGGED INTERPRETATION (`heart.healFraction`): "seal it in 5s or it heals 20%"
 * is 20% of MAX health, applied once. The phase latch means P3 follows either
 * way — a heal cannot walk the fight back into P2, which is what DESIGN's
 * "either way P3 follows" requires.
 */
export const ZONE6_BOSS = {
  id: 'gloomsovereign',
  name: 'THE GLOOM SOVEREIGN',

  hp: 1200,
  radius: 34,
  /** Chamber centre, in world px. */
  centre: { x: 22 * 32, y: 10 * 32 },
  /** DESIGN §5 Boss 6: the memories turn at 70% and 40%. */
  phase2At: 0.7,
  phase3At: 0.4,

  /** It walks its own chamber; it never sprints you down. */
  walk: { speed: 96, bandMin: 110, bandMax: 210, strafe: 0.45 },

  /** THE BUILD-READING. Escort waves counter the form you brought. */
  escorts: {
    interval: [7.5, 6.5, 6.0],
    liveCap: 14,
    bark: 'The Deep read your hand.',
    byWeapon: {
      // blade: rings you cannot stand inside — anti-melee zones
      cinderblade: { body: 'z6-wailer', count: 2, elite: null, note: 'grief rings' },
      // bow: shielded elites that are immune until you are within 3 tiles
      whisperbow: { body: 'z6-courtier', count: 2, elite: 'shielded', note: 'shielded blades' },
      // staff: chaff that eats bolts before they reach anything
      stormstaff: { body: 'z6-moth', count: 5, elite: null, note: 'moth chaff' },
      // maul: homing bolts that punish a slow swing
      bulwarkmaul: { body: 'z6-caster', count: 2, elite: null, note: 'homing casters' },
    } as Record<string, { body: string; count: number; elite: string | null; note: string }>,
  },

  // ------------------------------------------- P1 — Memory of the Flood
  /**
   * The chamber's four edges, as tile rects matching the arena's 'W' bands.
   * Order N, S, W, E; `pairs` indexes into this list — two edges at a time, on
   * an alternating beat you can learn.
   */
  edges: [
    { name: 'NORTH', x0: 1, y0: 1, x1: 42, y1: 3 },
    { name: 'SOUTH', x0: 1, y0: 17, x1: 42, y1: 19 },
    { name: 'WEST', x0: 1, y0: 1, x1: 5, y1: 19 },
    { name: 'EAST', x0: 38, y0: 1, x1: 42, y1: 19 },
  ],
  pairs: [
    [0, 1],
    [2, 3],
  ] as ReadonlyArray<readonly [number, number]>,
  flood: {
    /** Telegraph, flood, breath. Seconds, per phase. */
    telegraph: [2.0, 1.8, 1.6],
    duration: [3.6, 3.4, 3.2],
    gap: [4.2, 3.6, 3.0],
    damagePerSecond: 15,
    damageTickSeconds: 0.65,
    push: 205,
    pushTickSeconds: 0.3,
  },
  /** Brine-glob echoes: hand-rolled so the landing marker is real (boss 2). */
  glob: {
    interval: [3.6, 3.2, 3.0],
    count: [2, 3, 3],
    windup: 0.55,
    flightSeconds: 1.15,
    scatter: 78,
    splash: 48,
    damage: 15,
    arcHeight: 130,
    color: '#8fe36a',
  },
  /** P1's Mireskipper pours, from the two north corners of the dry floor. */
  pour: {
    interval: [5.0, 0, 0],
    count: [3, 0, 0],
    liveCap: 16,
    body: 'z6-skipper',
    tiles: [
      { tx: 8, ty: 5 },
      { tx: 35, ty: 5 },
    ],
  },

  // ------------------------------------------- P2 — Memory of the Court
  /** The rapier echo: boss 3's cadence, one beat tighter. */
  combo: {
    interval: [4.6, 3.4, 3.0],
    /** the red-glint tell; >= 0.4s is the readability law's floor */
    windup: [0.5, 0.44, 0.4],
    thrusts: [2, 3, 3],
    stepTime: 0.16,
    stepGap: [0.16, 0.13, 0.12],
    recover: 0.5,
    dashSpeed: 470,
    range: 62,
    arc: 1.25,
    damage: 15,
  },
  /** Escorts leave husks from P2 on. */
  huskFrom: 2,
  huskSeconds: 2,
  /** THE HUSK-HEART. Dropped once, at the 40% line. */
  heart: {
    defId: 'z6-huskheart',
    hp: 80,
    /** DESIGN: "seal it in 5s or it heals 20%". */
    windowSeconds: 5,
    healFraction: 0.2,
    /** Where it lands, relative to the chamber centre. */
    offsetY: -20,
  },

  // -------------------------------------------- P3 — Memory of the Dark
  dark: {
    /** The two braziers come up lit when the memory falls, and burn down. */
    brazierSeconds: 16,
    /** The Sovereign always glows: this is the radius of the hole it burns in
     *  the dark. Fairness is not a preference — it is never invisible. */
    glowRadius: 150,
  },
  /** Chained charges reusing boss 4's glint-lane telegraph language. */
  charge: {
    interval: [0, 0, 4.2],
    windup: [0, 0, 0.7],
    speed: [0, 0, 640],
    damage: 18,
    chain: [0, 0, 3],
    chainGap: 0.42,
    halfWidth: 30,
  },

  /** The kill: two full seconds of dying before the payout lands. */
  death: {
    seconds: 2,
    bursts: 5,
    burstGap: 0.34,
    shake: 16,
    shakeSeconds: 1.6,
    hitstop: 0.36,
    bark: 'The Deep will remember.',
  },
} as const

// ---------------------------------------------------------------- zone

export const ZONE6: ZoneConfig = {
  id: 6,
  name: 'The Gloomheart',
  tagline: 'Three trials, one clock, and the clock started without you.',
  palette: ZONE6_PALETTE,
  rooms: ZONE6_ROOMS,
  // Irrelevant under flow:'spurs' — the runner takes its rooms from spurIds.
  roomCount: 3,
  // With the meter opening at 40%, 80 seconds of combat is 48 to Arrival: a
  // three-spur zone is meant to end with the Gloom in the room.
  gloomFillSeconds: 80,
  // No timed vault in the Gloomheart: the zone IS the clock. Held high so the
  // shared vault machinery can never find anything to open.
  vaultClockSeconds: 99999,
  // DESIGN §5 Zone 6: "elite riders standard (~30% elite rate)".
  eliteRate: 0.3,
  eliteRiders: ['frenzied', 'shielded', 'gloomtouched', 'bloated'],
  // DESIGN §4's slope, five steps past zone 1: 1.12^5 = 1.76, 1.10^5 = 1.61.
  statScale: { hp: 1.76, damage: 1.61 },
  // The end of the healing-scarcity curve (DESIGN §11): zone 5 sold it to you,
  // and down here there is mostly nothing.
  foodPerRoom: [0, 1],
  music: {
    root: 36,
    mode: 'phrygian',
    tempo: 54,
    padLevel: 1,
    bassLevel: 0.9,
    percLevel: 0.3,
    // 'depths'-adjacent, and slower than the mines by eight beats — the same
    // room, remembered wrong.
    character: 'depths',
  },
  bossId: 'gloomsovereign',
  // D-021: the Heartchamber hub, three spurs, three Heartseals.
  flow: 'spurs',
  spurIds: ['z6-drowning-spur', 'z6-pale-spur', 'z6-dark-spur'],
}
